/**
 * NudgeGateProvider — Semaphore for concurrent nudge display
 *
 * Limits the number of nudges displayed simultaneously to MAX_CONCURRENT (3).
 * Uses a priority queue so higher-priority nudges get slots first when multiple
 * nudges want to show on the same page.
 *
 * Pattern: each component that wants to show a nudge calls useNudgeSlot(nudgeId, wantToShow).
 * If it gets a slot, it calls recordImpression and renders the nudge.
 * When the component unmounts or wantToShow flips false, the slot is released
 * and waiting nudges can acquire it.
 */

import { useRef, useState, useCallback, type ReactNode } from 'react';
import {
  NudgeGateContext,
  type NudgeSlotInfo,
  type NudgeGateContextValue,
} from './NudgeGateContext';

const MAX_CONCURRENT = 3;

interface NudgeGateProviderProps {
  children: ReactNode;
}

export function NudgeGateProvider({ children }: NudgeGateProviderProps) {
  // Ref for synchronous checks (no stale closure issues)
  const activeNudgesRef = useRef<Map<string, NudgeSlotInfo>>(new Map());

  // State only for triggering re-renders of consumers
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [_tick, setRerender] = useState(0);

  const triggerRerender = useCallback(() => {
    setRerender((prev) => prev + 1);
  }, []);

  const hasSlot = useCallback((nudgeId: string): boolean => {
    return activeNudgesRef.current.has(nudgeId);
  }, []);

  const getAvailableSlots = useCallback((): number => {
    return Math.max(0, MAX_CONCURRENT - activeNudgesRef.current.size);
  }, []);

  const requestSlot = useCallback(
    (nudgeId: string, priority: number): void => {
      const active = activeNudgesRef.current;

      // Already has a slot
      if (active.has(nudgeId)) {
        return;
      }

      // Slots available — grant immediately
      if (active.size < MAX_CONCURRENT) {
        active.set(nudgeId, { nudgeId, priority, requestedAt: Date.now() });
        triggerRerender();
        return;
      }

      // No slots available — try to bump a lower-priority nudge
      let lowestId: string | null = null;
      let lowestPriority = priority; // Only bump if our priority is higher

      for (const [id, info] of active) {
        if (info.priority < lowestPriority) {
          lowestId = id;
          lowestPriority = info.priority;
        }
      }

      if (lowestId && lowestPriority < priority) {
        // Bump the lower-priority nudge
        active.delete(lowestId);
        active.set(nudgeId, { nudgeId, priority, requestedAt: Date.now() });
        triggerRerender();
      }
      // Otherwise, no slot available and not high enough priority to bump — denied
    },
    [triggerRerender]
  );

  const releaseSlot = useCallback(
    (nudgeId: string): void => {
      const active = activeNudgesRef.current;
      if (active.has(nudgeId)) {
        active.delete(nudgeId);
        triggerRerender();
      }
    },
    [triggerRerender]
  );

  const value: NudgeGateContextValue = {
    hasSlot,
    requestSlot,
    releaseSlot,
    getAvailableSlots,
  };

  return <NudgeGateContext.Provider value={value}>{children}</NudgeGateContext.Provider>;
}
